import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { t } from 'i18next'
import { AlertState, hideAlert } from './alert-slice'
import React, { ReactNode } from 'react'
import Text from 'app/app/(client)/components/atoms/Text/Text'
import { ApiError } from 'app/services/common-error-utils'
import { FALLBACK_TRANSLATION } from 'app/app/(client)/utils/client-constants'
import ProgressBar from 'app/app/(client)/components/molecules/ProgressBar/ProgressBar'

export type AlertBoxState = Omit<AlertState, 'isVisible'>

export type AlertOptions = Partial<{
  description: string
  title: string
  onPrimary: () => void
  onSecondary: () => void
  loadingPrimary: boolean
}>

const waitSection = (waitMessage: string): ReactNode => (
  <div className="flex flex-col justify-center items-center gap-2 mt-3 mb-4">
    <Text id="wait-text">{t(waitMessage)}</Text>
    <ProgressBar value={0} label={''} className="custom-progress" progressValueVisible={false} />
  </div>
)

const apiErrorDescription = (error: ApiError): ReactNode => {
  const causeText =
    error.cause == null
      ? undefined
      : typeof error.cause === 'string'
        ? error.cause
        : (error.cause as Error).message ?? undefined

  const clientReqId = error.clientReqId
  const stackTrace = error.stack

  return (
    <div className="flex flex-col gap-3 mt-2 mb-3">
      {error.status ? (
        <div className="flex flex-col gap-1">
          <Text className="font-semibold text-sm text-gray-700">Status:</Text>
          <Text className="text-sm text-gray-600">{error.status}</Text>
        </div>
      ) : null}
      {error.message ? (
        <div className="flex flex-col gap-1">
          <Text className="font-semibold text-sm text-gray-700">Message:</Text>
          <Text className="text-sm text-gray-600">{error.message}</Text>
        </div>
      ) : null}
      {clientReqId ? (
        <div className="flex flex-col gap-1">
          <Text className="font-semibold text-sm text-gray-700">Reference ID:</Text>
          <Text className="text-sm text-gray-600 break-all">{clientReqId}</Text>
        </div>
      ) : null}
      {causeText ? (
        <div className="flex flex-col gap-1">
          <Text className="font-semibold text-sm text-gray-700">Cause:</Text>
          <pre className="text-xs text-gray-600 whitespace-pre-wrap wrap-break-word bg-gray-50 rounded p-2 max-h-24 overflow-y-auto">{causeText}</pre>
        </div>
      ) : null}
      {stackTrace ? (
        <div className="flex flex-col gap-1">
          <Text className="font-semibold text-sm text-gray-700">Stack Trace:</Text>
          <pre className="text-xs text-gray-600 whitespace-pre-wrap wrap-break-word bg-gray-50 rounded p-2 max-h-32 overflow-y-auto">{stackTrace}</pre>
        </div>
      ) : null}
    </div>
  )
}
export const waitDialog = (waitMessage: string): AlertBoxState => ({
  id: 'wait-dialog',
  title: t('dialog.waitTitle'),
  description: waitSection(waitMessage),
  isDismissOnBackdrop: false,
  type: 'modal'
})

const errorBaseDialog = (message: string, title?: string, type?: 'error' | 'warning' | 'info'): AlertBoxState => ({
  id: 'error-alert',
  title: title || t('errors.errorTitle') || 'Error notification',
  description: message,
  type: type || 'error'
})

export const configErrorPopup = (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction> | undefined,
  message: string,
  title?: string,
  onPrimary?: () => void
): AlertBoxState => ({
  ...errorBaseDialog(message, title),
  onPrimaryClick: () => {
    onPrimary && onPrimary()
  },
  primaryButtonContent: 'Reload',
  isDismissOnBackdrop: false,
  onSecondaryClick: () => {
    dispatch && dispatch(hideAlert())
  }
})

export const errorAlert = (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction> | undefined,
  message: string,
  title?: string,
  onSecondary?: () => void,
  onPrimary?: () => void,
  type?: 'error' | 'warning' | 'info'
): AlertBoxState => ({
  ...errorBaseDialog(message, title, type),
  onPrimaryClick: () => {
    onPrimary && onPrimary()
    dispatch && dispatch(hideAlert())
  },
  onSecondaryClick: () => {
    onSecondary && onSecondary()
    dispatch && dispatch(hideAlert())
  }
})

export const languageChangeModalProp = (
  description: React.ReactNode,
  onPrimary: () => void,
  isLoadingPrimaryButton: boolean,
  onClose: () => void
): AlertBoxState => ({
  title: t('languageSelect.changeLanguage'),
  id: 'language-selection-alert',
  description,
  type: 'modal',
  onPrimaryClick: onPrimary,
  onClose,
  isLoadingPrimaryButton
})

export const confirmResetDialogProps = (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction> | undefined,
  onPrimary: () => void
): AlertBoxState => ({
  title: t('dialog.confirmTitle'),
  id: 'confirm-reset-dialog',
  description: t('review.salesforceSyncConfirm'),
  onPrimaryClick: () => {
    onPrimary()
    dispatch && dispatch(hideAlert())
  },
  type: 'info'
})

export const apiErrorAlert = (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction> | undefined,
  error: ApiError,
  onPrimary?: () => void
): AlertBoxState => ({
  type: 'error',
  id: 'api-error-alert',
  title: error.type || error.name || t('errors.errorTitle') || 'Error',
  description: apiErrorDescription(error),
  onPrimaryClick: () => {
    onPrimary && onPrimary()
    dispatch && dispatch(hideAlert())
  }
})

export const sessionExpiredProps = (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction> | undefined,
  onPrimary?: () => void
): AlertBoxState => ({
  type: 'error',
  id: 'session-expired-alert',
  title: t('login.sessionExpiredTitle') || FALLBACK_TRANSLATION.sessionExpiredTitle,
  description: t('login.sessionExpired') || FALLBACK_TRANSLATION.sessionExpiredMessage,
  primaryButtonContent: t('login.sessionExpiredOkButton') || FALLBACK_TRANSLATION.login,
  onPrimaryClick: () => {
    onPrimary && onPrimary()
    dispatch && dispatch(hideAlert())
  }
})

export const logoutAlertProps = (isLoading: boolean, message: string, onPrimary: () => void): AlertBoxState => ({
  id: 'logout-warning',
  title: t('loggedOut.verification'),
  description: message || t('loggedOut.clear'),
  type: 'modal',
  onPrimaryClick: onPrimary,
  isLoadingPrimaryButton: isLoading,
  deactivateCloseButton: isLoading
})

export const basicErrorAlertProps = ({ title, description, onPrimary, onSecondary }: AlertOptions): AlertBoxState => ({
  id: 'basic-error-alert',
  title: title || t('errors.errorTitle'),
  description,
  type: 'error',
  onPrimaryClick: onPrimary ? onPrimary : undefined,
  onSecondaryClick: onSecondary ? onSecondary : undefined,
  isDismissOnBackdrop: true
})

export const basicInfoAlertProps = ({
  title,
  description,
  onPrimary,
  loadingPrimary,
  onSecondary
}: AlertOptions): AlertBoxState => ({
  id: 'basic-info-alert',
  title: title || t('dialog.waitTitle'),
  description,
  type: 'info',
  onPrimaryClick: onPrimary ? onPrimary : undefined,
  onSecondaryClick: onSecondary ? onSecondary : undefined,
  isDismissOnBackdrop: true,
  isLoadingPrimaryButton: loadingPrimary ? loadingPrimary : false,
  isWaitForAction: true
})

export const alertWithUi = (description: React.ReactNode, title?: string, onPrimary?: () => void): AlertBoxState => ({
  id: 'validate-offer-alert',
  title: title || t('info.infoTitle'),
  description,
  type: 'modal',
  onPrimaryClick: () => (onPrimary ? onPrimary() : undefined),
  isDismissOnBackdrop: true
})

export const documentTemplateSelctionAlert = (
  description: React.ReactNode,
  title: string,
  onPrimary: () => void,
  onClose: () => void,
  isGendocXMLDownloadInProgress: boolean
): AlertBoxState => ({
  id: 'document-template-selection-alert',
  title: title || t('info.infoTitle'),
  description,
  type: 'modal',
  onPrimaryClick: () => (onPrimary ? onPrimary() : undefined),
  onClose,
  isWaitForAction: isGendocXMLDownloadInProgress ? false : true
})
